import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Users,
  CalendarCheck,
  FileText,
  Pill,
  Phone,
  Stethoscope,
  BedDouble,
  LogOut,
  FlaskRound,
  X,
  ShoppingCart,
  Package,
  Receipt,
  Plus
} from "lucide-react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import "../components/Dashboard.css";
import axios from "axios";
import { hasPermission } from "../utils/permissions";


const StatCardNew = ({ icon: Icon, title, value, change, colorClass, onClick }) => (
  <div
    className={`card ${colorClass}`}
    onClick={onClick}
    style={{ cursor: onClick ? "pointer" : "default" }}
  >
    <div className="card-header">
      <div className="card-header-left">
        <span className="card-icon">
          <Icon style={{ width: 24, height: 24 }} />
        </span>
        <span className="card-title">{title}</span>
      </div>
      {typeof change === "string" && (
        <span className={`card-change ${change.startsWith("+") ? "positive" : "negative"}`}>
          {change} {change.startsWith("+") ? "↑" : "↓"}
        </span>
      )}
    </div>
    <div className="card-value">{value}</div>
  </div>
);

const StatCard = ({ icon: Icon, title, value, change, colorClass }) => (
  <div className={`card ${colorClass}`}>
    <div className="card-header">
      <div className="card-header-left">
        <span className="card-icon">
          <Icon style={{ width: 24, height: 24 }} />
        </span>
        <span className="card-title">{title}</span>
      </div>
      {typeof change === "string" && (
        <span className={`card-change ${change.startsWith("+") ? "positive" : "negative"}`}>
          {change} {change.startsWith("+") ? "↑" : "↓"}
        </span>
      )}
    </div>
    <div className="card-value">{value}</div>
  </div>
);

const App = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [orderTotal, setOrderTotal] = useState(0);

  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const [chartSummary, setChartSummary] = useState(null);

  const [pieChartData, setPieChartData] = useState([]);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(true);
  const [diagnosisStats, setDiagnosisStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
const [canSeeNotepad, setCanSeeNotepad] = useState(false);
const [canSeeWetaOrders, setCanSeeWetaOrders] = useState(false);
const [todayOrder, setTodayOrder] = useState(0);
  // Helper: get token from localStorage
  const getToken = () =>
    localStorage.getItem("session_token") ||
    localStorage.getItem("token") ||
    null;

  // Helper: Append token to URL
  const appendTokenToUrl = (url) => {
    const token = getToken();
    if (!token) return url;
    
    try {
      const urlObj = new URL(url);
      if (!urlObj.searchParams.has("token")) {
        urlObj.searchParams.append("token", token);
      }
      return urlObj.toString();
    } catch (error) {
      // If URL parsing fails, append manually
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}token=${encodeURIComponent(token)}`;
    }
  };

  // Helper: Get auth headers
  const getAuthHeaders = () => {
    const token = getToken();
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      headers["X-Session-Token"] = token;
    }
    
    return headers;
  };
useEffect(() => {
  const loadPermissions = async () => {
    try {
      const notepad = await hasPermission("folders", "notepadview");
      const weta = await hasPermission("folders", "wetaorder"); // adjust keys as needed

      setCanSeeNotepad(!!notepad);
      setCanSeeWetaOrders(!!weta);
    } catch (err) {
      console.error("Error loading permissions:", err);
    }
  };

  loadPermissions();

  const handlePermissionUpdate = () => loadPermissions();
  window.addEventListener("permissionsUpdated", handlePermissionUpdate);

  return () =>
    window.removeEventListener("permissionsUpdated", handlePermissionUpdate);
}, []);

  // Fetch top recent leads (Appointment table) - branch-wise via token
  useEffect(() => {
const fetchAppointments = async () => {
  try {
    setLoadingAppointments(true);

    const apiUrl = appendTokenToUrl(
      "https://newerp.awasthivitality.com/api/lead/show"
    );

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    const data = await response.json();

    if (data.success && data.data) {

      const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD

   const formatted = data.data
.map((lead) => {
  let time = "—";

  if (lead.created_at) {
    const parts = lead.created_at.split(" "); // ["14-04-2026", "09:34:23"]

    if (parts.length === 2) {
      const timePart = parts[1]; // "09:34:23"
      const [hour, minute] = timePart.split(":");

      let h = parseInt(hour);
      const ampm = h >= 12 ? "PM" : "AM";

      h = h % 12 || 12;

      time = `${h}:${minute} ${ampm}`;
    }
  }

  return {
    name: lead.lead_name,
    mobile: lead.lead_phone,
    date: lead.lead_date
      ? new Date(lead.lead_date).toLocaleDateString("en-CA")
      : null,
    time: time,
    lead_status_name: lead.status_text || "In Progress",
  };
})
  // ✅ filter today's + remove "In Progress"
 .filter(
  (lead) =>
    lead.date === today &&
    (lead.lead_status_name || "").toLowerCase() === "available"
)
  .sort((a, b) => new Date(b.date) - new Date(a.date))
  .slice(0, 3);

      setAppointments(formatted);
    } else {
      setAppointments([]);
    }
  } catch (error) {
    console.error("Error fetching appointments:", error);
    setAppointments([]);
  } finally {
    setLoadingAppointments(false);
  }
};

    fetchAppointments();
  }, []);

  // External Weta orders
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const response = await axios.get("https://wetaworldz.com/api/order-list");
        if (response.data && typeof response.data.orderTotal !== "undefined") {
          setOrderTotal(response.data.orderTotal);
                setTodayOrder(response.data.todayOrder || 0); // ✅ ADD THIS
        } else {
          console.warn("Unexpected Weta API response:", response.data);
        }
      } catch (error) {
        console.error("Error fetching Weta orders:", error);
      }
    };

    fetchOrderData();
  }, []);

  // Fetch diagnosis pie chart data (branch‑wise via token)
  useEffect(() => {
    const fetchDiagnosisData = async () => {
      try {
        setLoadingDiagnosis(true);
        const apiUrl = appendTokenToUrl("https://newerp.awasthivitality.com/api/dashboard/top-diagnoses");
        
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        const result = await response.json();
        console.log("Diagnosis API result:", result);

        let dataArray = [];

        if (Array.isArray(result)) {
          dataArray = result;
        } else if (result.success && Array.isArray(result.data)) {
          dataArray = result.data;
        }

        if (dataArray.length > 0) {
          const formattedData = dataArray.map((item) => ({
            name: item.name,
            value: item.percentage ?? item.value ?? 0,
            color: item.color || "#8884d8"
          }));

          setPieChartData(formattedData);
          setDiagnosisStats({ last_updated: new Date().toISOString() });
        } else {
          console.warn("No diagnosis data found:", result);
          setPieChartData([]);
        }
      } catch (error) {
        console.error("Error fetching diagnosis data:", error);
        setPieChartData([]);
      } finally {
        setLoadingDiagnosis(false);
      }
    };

    fetchDiagnosisData();
    const interval = setInterval(fetchDiagnosisData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch monthly patient visits (graph) – branch‑wise via token
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoadingChart(true);
        const apiUrl = appendTokenToUrl("https://newerp.awasthivitality.com/api/dashboard/monthly-patient-visits");
        
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        const data = await response.json();
        
        if (data && data.success && Array.isArray(data.data)) {
          const raw = data.data;
          const byYearMonth = new Map();

          for (const d of raw) {
            const ym =
              d.year_month ||
              (d.year && d.month
                ? `${d.year}-${String(d.month).padStart(2, "0")}`
                : undefined);
            if (!ym) continue;
            byYearMonth.set(ym, {
              month: d.month,
              year: d.year,
              month_name: d.month_name || d.name || d.display_name,
              full_date: d.full_date,
              visits: d["Patient Visits"] ?? d.patient_visits ?? d.count ?? 0
            });
          }

          const now = new Date();
          const seq = [];
          for (let i = 3; i >= 0; i--) {
            const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const y = dt.getFullYear();
            const m = dt.getMonth() + 1;
            const ym = `${y}-${String(m).padStart(2, "0")}`;
            const monthNames = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec"
            ];
            const base = byYearMonth.get(ym);
            seq.push({
              name: `${monthNames[m - 1]} ${y}`,
              month: m,
              year: y,
              year_month: ym,
              full_date: base?.full_date || `${monthNames[m - 1]} ${y}`,
              "Patient Visits": base?.visits || 0
            });
          }

          setChartData(seq);
          const total = seq.reduce((s, x) => s + (x["Patient Visits"] || 0), 0);
          setChartSummary(
            data.summary || {
              total_visits: total,
              average_visits: seq.length
                ? Math.round((total / seq.length) * 10) / 10
                : 0,
              max_visits: seq.reduce(
                (m, x) => Math.max(m, x["Patient Visits"] || 0),
                0
              )
            }
          );
        } else {
          setChartData([]);
        }
      } catch (err) {
        console.error("Error fetching chart data:", err);
        setChartData([]);
      } finally {
        setLoadingChart(false);
      }
    };

    fetchChartData();
  }, []);

  // Fetch recent activities – branch‑wise via token
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoadingActivities(true);
        const apiUrl = appendTokenToUrl("https://newerp.awasthivitality.com/api/dashboard/recent-activities");
        
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        const data = await response.json();
        
        if (data.success && data.data) {
          // Format activities with time display
          const formattedActivities = data.data.map((activity) => {
            const timestamp = activity.timestamp || activity.created_at;
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            let timeDisplay = "";
            if (diffMins < 1) {
              timeDisplay = "Just now";
            } else if (diffMins < 60) {
              timeDisplay = `${diffMins}m ago`;
            } else if (diffHours < 24) {
              timeDisplay = `${diffHours}h ago`;
            } else if (diffDays < 7) {
              timeDisplay = `${diffDays}d ago`;
            } else {
              timeDisplay = date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              });
            }

            return {
              ...activity,
              time: timeDisplay,
              status: activity.action || activity.type || "Activity"
            };
          });

          setActivities(formattedActivities);
        } else {
          setActivities([]);
        }
      } catch (err) {
        console.error("Error fetching recent activities:", err);
        setActivities([]);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, []);

  // Fetch statistics – branch‑wise via token
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const apiUrl = appendTokenToUrl("https://newerp.awasthivitality.com/api/dashboard/statistics");
        
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        const data = await response.json();
        
        if (data.success) {
          setStats(data.data);
        } else {
          setStats(null);
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getAppointmentBadgeClass = (status) => {
    if (!status) return "appointment-badge";
    const s = status.trim().toLowerCase();
    switch (s) {
      case "running":
        return "appointment-badge appointment-running";
      case "win":
        return "appointment-badge appointment-win";
      case "lost":
        return "appointment-badge appointment-lost";
      default:
        return "appointment-badge";
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{data.name}</p>
          <p style={{ margin: "5px 0 0 0", color: "#666" }}>
            {data.count || data.value} patients ({data.value}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        {/* Top Section */}
        <div className="top-section-new">
          {/* Main Stats Card */}
          <div className="main-stats-card d-flex">
            <div className="container">
              <h2 className="card-title">Hospital Dashboard</h2>
              <p className="card-subtitle">Overview of Latest Month</p>
              <div className="card-body">
                <h3 className="stat-value">
                  {loading ? "..." : stats?.total_patients_this_month || 0}
                </h3>
                <p className="stat-label">Total Patient visits this month</p>
                <h3 className="stat-value-sm">
                  {loading ? "..." : stats?.total_leads_this_month || 0}
                </h3>
                <p className="stat-label">Total Leads of this month</p>
                <button
                  className="summary-btn"
                  onClick={() => navigate("/LeadManagement")}
                >
                  View Full Leads
                </button>
              </div>
            </div>
          </div>

          {/* Monthly Patient Visits Chart */}
          <div className="chart-card">
            <h3 className="card-title">Monthly Patient Visits</h3>
            <div className="chart-content">
              {loadingChart ? (
                <div
                  style={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p>Loading chart data...</p>
                </div>
              ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 5, right: 30, left: -20, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2fb2e7" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#2fb2e7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      vertical={false}
                      horizontal={false}
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      stroke="#9ca3af"
                      fontSize={12}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      stroke="#9ca3af"
                      fontSize={12}
                    />
                    <Tooltip
                      formatter={(value) => [value, "Patient Visits"]}
                      labelFormatter={(label, payload) => {
                        const p =
                          Array.isArray(payload) && payload.length
                            ? payload[0].payload
                            : null;
                        return p && p.full_date
                          ? `Month: ${p.full_date}`
                          : `Month: ${label}`;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="Patient Visits"
                      stroke="#2fb2e7"
                      fillOpacity={1}
                      fill="url(#colorPv)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p>No chart data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Diagnosis Distribution Pie Chart */}
          <div className="chart-card traffic-card">
            <h3 className="card-title">Patient Distribution by Diagnosis</h3>
            <div className="chart-content relative">
              {loadingDiagnosis ? (
                <div
                  style={{
                    height: 150,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p>Loading diagnosis data...</p>
                </div>
              ) : pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      innerRadius={40}
                      fill="#8884d8"
                      stroke="none"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    height: 150,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <p>No diagnosis data available</p>
                </div>
              )}
              <div className="pie-chart-inner-circle"></div>
            </div>
            <div className="pie-chart-legend">
              {pieChartData.map((item, index) => (
                <div key={index} className="legend-item">
                  <p className="legend-value">{item.value}%</p>
                  <p className="legend-name">{item.name}</p>
                </div>
              ))}
            </div>
            {diagnosisStats && (
              <div
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "10px"
                }}
              >
                Last updated:{" "}
                {new Date(diagnosisStats.last_updated).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Statistics Cards Grid */}
        <div
          className="stat-card-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "0.5rem",
            margin: "1.5rem 0"
          }}
        >
          {loading ? (
            <p>Loading stats...</p>
          ) : stats ? (
            <>
      {/* Notepad - Permission Check */}
      {canSeeNotepad && (
  <StatCardNew
    icon={FileText}
    title="Notepad"
    value={stats.total_notepad}
    colorClass="bg-blue-indigo"
    onClick={() => navigate("/PatientNotepad")}
  />
)}
              <StatCardNew
                icon={FileText}
                title="Local Notepad"
                value={stats.total_local_notepad}
                colorClass="bg-green-cyan"
                onClick={() => navigate("/LocalNotepad")}
              />
              <StatCardNew
                icon={Users}
                title="Total Patients"
                value={stats.total_patients}
                colorClass="bg-orange-red"
                onClick={() => navigate("/PatientList")}
              />
              {/* <StatCardNew
                icon={ShoppingCart}
                title="Total Orders"
                value={stats.total_orders}
                colorClass="bg-gray-blue"
                onClick={() => navigate("/OrderList")}
              /> */}
              <StatCardNew
                icon={Package}
                title="Total Couriers"
                value={stats.total_couriers}
                colorClass="bg-yellow-orange"
                onClick={() => navigate("/CourierEntryList")}
              />
              <StatCardNew
                icon={Receipt}
                title="Total Invoices"
                value={stats.total_invoices}
                colorClass="bg-purple-pink"
                onClick={() => navigate("/InvoiceList")}
              />
               {canSeeWetaOrders && (
  // <StatCardNew
  //   icon={FileText}
  //   title="Weta Orders"
  //   value={orderTotal}
  //   colorClass="bg-purple-blue"
  //   onClick={() => navigate("/WetaOrderList")}
  // />
<StatCardNew
  icon={FileText}
  title="Weta Orders"
  value={
    <>
      {orderTotal}{" "}
      <span style={{ fontSize: "14px", opacity: 0.7 }}>
        (Today: {todayOrder})
      </span>
    </>
  }
  colorClass="bg-purple-blue"
  onClick={() => navigate("/WetaOrderList")}
/>
)}

            </>
          ) : (
            <p>Failed to load stats.</p>
          )}
        </div>

        {/* Bottom Section */}
        <div className="bottom-section">
          {/* Recent Activities */}
          <div className="activities-card">
            <div className="activities-header">
              <h3 className="card-title">Recent Activities</h3>
              <button className="more-btn" onClick={() => navigate("/AllActivities")}>
                More
              </button>
            </div>
            {loadingActivities && <p>Loading activities...</p>}
            <ul className="activities-list">
              {!loadingActivities && activities.length > 0 ? (
                activities.map((activity, index) => {
                  const text = `${activity.status} ${activity.description}`.toLowerCase();
                  let Icon = FileText;
                  if (text.includes("patient")) Icon = User;
                  else if (text.includes("lead")) Icon = Users;
                  else if (text.includes("appointment")) Icon = CalendarCheck;
                  else if (
                    text.includes("medicine") ||
                    text.includes("drug") ||
                    text.includes("prescription")
                  )
                    Icon = Pill;
                  else if (text.includes("consult") || text.includes("doctor"))
                    Icon = Stethoscope;
                  else if (text.includes("admit") || text.includes("admission"))
                    Icon = BedDouble;
                  else if (text.includes("discharge")) Icon = LogOut;
                  else if (text.includes("follow")) Icon = Phone;
                  else if (text.includes("test") || text.includes("lab"))
                    Icon = FlaskRound;

                  const bgColorClass = [
                    "icon-blue-bg",
                    "icon-green-bg",
                    "icon-purple-bg",
                    "icon-orange-bg",
                    "icon-pink-bg"
                  ][index % 5];

                  return (
                    <li key={index} className="activity-item">
                      <div className={`activity-icon-wrapper ${bgColorClass}`}>
                        <Icon className="activity-icon" />
                      </div>
                      <div>
                        <p className="activity-time">{activity.time}</p>
                        <p className="activity-status">{activity.status}</p>
                        <p className="activity-description">
                          {activity.description}
                        </p>
                      </div>
                    </li>
                  );
                })
              ) : !loadingActivities ? (
                <li>No recent activities found.</li>
              ) : null}
            </ul>
          </div>

          {/* Appointment Status Table */}
          <div className="order-status-card">
            <div className="order-card-header">
              <div>
                <h3 className="card-title">Appointment Status</h3>
                <p className="card-subtitle">Overview of Latest Leads</p>
              </div>
              <div className="order-header-actions">
                <div className="action-buttons">
                  <button
                    className="add-btn"
                    onClick={() => navigate("/AddLocalNotepad")}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="table-wrapper">
              <table className="order-table">
                <thead>
  <tr>
    {["S.No", "Name", "Mobile", "Date", "Time", "Status"].map((header, index) => (
      <th key={index} className="table-header">
        {header}
      </th>
    ))}
  </tr>
</thead>
                <tbody>
                  {loadingAppointments ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center" }}>
                        Loading appointments...
                      </td>
                    </tr>
                  ) : appointments.length > 0 ? (
                    appointments.map((appt, index) => (
                      <tr key={index}>
                                <td className="table-data">{index + 1}</td> {/* ✅ S.No */}

                        <td className="table-data font-medium text-gray-900">
                          {appt.name}
                        </td>
                        <td className="table-data">{appt.mobile}</td>
                        <td className="table-data">{appt.date}</td>
                        <td className="table-data">{appt.time}</td>
                        <td className="table-data">
                          <span className={getAppointmentBadgeClass(appt.lead_status_name)}>
                            {appt.lead_status_name}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center" }}>
                        No appointments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default App;